import { LEAD_STATUSES, PRE_LEAD_COLOR, type Billing, type LeadStatus } from '@delvero/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { StatusSelect } from '../../components/Status';
import { PipelineBoard } from './PipelineBoard';
import { fmtMin, fmtZl, relDate } from '../../lib/format';
import { orpc } from '../../lib/orpc';
import { useShell } from '../../shell';

type Filter = 'all' | 'pre' | LeadStatus;

export function PipelineView() {
  const { activeAppId, openLead, setCallTarget } = useShell();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [view, setView] = useState<'table' | 'board'>('table');

  const leadsQuery = useQuery({
    ...orpc.leads.list.queryOptions({ input: { appId: activeAppId ?? '' } }),
    enabled: !!activeAppId,
  });
  const researchQuery = useQuery({
    ...orpc.research.list.queryOptions({ input: { appId: activeAppId ?? '' } }),
    enabled: !!activeAppId,
  });

  const invalidateLeads = () => {
    void queryClient.invalidateQueries({ queryKey: orpc.leads.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.research.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.dashboard.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.costs.key() });
  };

  const updateLead = useMutation(orpc.leads.update.mutationOptions({ onSuccess: invalidateLeads }));
  const promote = useMutation(orpc.research.promote.mutationOptions({ onSuccess: invalidateLeads }));

  if (!activeAppId) {
    return (
      <div className="view">
        <div className="card center-pad">
          <span className="spinner" /> Wczytywanie…
        </div>
      </div>
    );
  }

  const leads = leadsQuery.data ?? [];
  const research = researchQuery.data ?? [];

  const promotePre = (id: string, andCall: boolean) => {
    promote.mutate(
      { id, andCall },
      {
        onSuccess: (lead) => {
          if (andCall) {
            setCallTarget(lead.id);
            void navigate({ to: '/calling' });
          } else {
            openLead(lead.id);
          }
        },
      },
    );
  };

  const counts: Partial<Record<LeadStatus, number>> = {};
  for (const l of leads) counts[l.status] = (counts[l.status] ?? 0) + 1;

  const term = q.trim().toLowerCase();
  let rows = leads.filter((l) => filter === 'all' || l.status === filter);
  const showPre = filter === 'all' || filter === 'pre';
  let preRows = showPre ? research.slice() : [];
  if (term) {
    rows = rows.filter((l) => `${l.company} ${l.contact}`.toLowerCase().includes(term));
    preRows = preRows.filter((r) => `${r.company} ${r.email}`.toLowerCase().includes(term));
  }
  // Leads arrive sorted by lastActivity desc; research by createdAt desc.
  preRows.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  const boardLeads = term
    ? leads.filter((l) => `${l.company} ${l.contact}`.toLowerCase().includes(term))
    : leads;

  return (
    <div className="view">
      <div className="filter-row">
        <div className="seg" role="group" aria-label="Widok listy">
          <button
            type="button"
            className={view === 'table' ? 'on' : ''}
            aria-pressed={view === 'table'}
            onClick={() => setView('table')}
          >
            Tabela
          </button>
          <button
            type="button"
            className={view === 'board' ? 'on' : ''}
            aria-pressed={view === 'board'}
            onClick={() => setView('board')}
          >
            Tablica
          </button>
        </div>
        {view === 'table' ? (
          <>
            <button className={`chip${filter === 'all' ? ' chip-on' : ''}`} onClick={() => setFilter('all')}>
              Wszystkie <b className="mono">{leads.length + research.length}</b>
            </button>
            <button
              className={`chip${filter === 'pre' ? ' chip-on' : ''}`}
              onClick={() => setFilter(filter === 'pre' ? 'all' : 'pre')}
            >
              <span className="dot" style={{ background: PRE_LEAD_COLOR }} />
              Pre-lead <b className="mono">{research.length}</b>
            </button>
            {LEAD_STATUSES.map((s) => (
              <button
                key={s.id}
                className={`chip${filter === s.id ? ' chip-on' : ''}`}
                onClick={() => setFilter(filter === s.id ? 'all' : s.id)}
              >
                <span className="dot" style={{ background: s.color }} />
                {s.label} <b className="mono">{counts[s.id] ?? 0}</b>
              </button>
            ))}
          </>
        ) : null}
        <div className="filter-spacer" />
        <div className="search-box">
          <Icon name="search" size={13} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj firmy lub osoby…" />
        </div>
        <button className="btn primary" onClick={() => setAdding(true)}>
          <Icon name="plus" size={13} /> Nowy lead
        </button>
      </div>

      {view === 'board' ? (
        <PipelineBoard leads={boardLeads} appId={activeAppId} onOpen={openLead} />
      ) : (
      <div className="card table-card">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: '26%' }}>Firma / kontakt</th>
              <th>Status</th>
              <th>Plan</th>
              <th>Źródło</th>
              <th className="num">Calle</th>
              <th className="num">CAC</th>
              <th className="num">MRR</th>
              <th className="num">Ostatnio</th>
            </tr>
          </thead>
          <tbody>
            {preRows.map((r) => (
              <tr key={r.id} className="no-click pre-row">
                <td>
                  <div className="cell-main">{r.company}</div>
                  <div className="cell-sub">{[r.phone, r.email].filter(Boolean).join(' · ') || 'brak kontaktu'}</div>
                </td>
                <td>
                  <span
                    className="badge"
                    style={{
                      color: PRE_LEAD_COLOR,
                      background: 'rgba(138,141,156,0.10)',
                      borderColor: 'rgba(138,141,156,0.24)',
                    }}
                  >
                    <span className="dot" style={{ background: PRE_LEAD_COLOR }} />
                    Pre-lead
                  </span>
                </td>
                <td>
                  <span className="cell-sub">—</span>
                </td>
                <td>
                  <span className="cell-sub">Research</span>
                </td>
                <td className="num mono">—</td>
                <td className="num mono">—</td>
                <td className="num mono">—</td>
                <td className="num">
                  <div className="row-actions">
                    {r.phone ? (
                      <button
                        className="btn slim2 primary"
                        title="Zadzwoń teraz"
                        disabled={promote.isPending}
                        onClick={() => promotePre(r.id, true)}
                      >
                        <Icon name="phone" size={12} />
                      </button>
                    ) : null}
                    <button className="btn slim2" disabled={promote.isPending} onClick={() => promotePre(r.id, false)}>
                      Kwalifikuj <Icon name="arrow" size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {rows.map((l) => (
              <tr key={l.id} onClick={() => openLead(l.id)}>
                <td>
                  <div className="cell-main">{l.company}</div>
                  <div className="cell-sub">
                    {l.contact} · {l.role}
                  </div>
                </td>
                <td>
                  <StatusSelect
                    value={l.status}
                    onChange={(v: LeadStatus) => updateLead.mutate({ id: l.id, patch: { status: v } })}
                  />
                </td>
                <td>
                  {l.planName ? (
                    <span>
                      {l.planName} <span className="cell-sub mono">{fmtZl(l.planPrice ?? 0)}/mc</span>
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <span className="cell-sub">{l.campaignName ?? '—'}</span>
                </td>
                <td className="num mono">
                  {l.econ.callCount > 0 ? `${l.econ.callCount} · ${fmtMin(l.econ.callSec)}` : '—'}
                </td>
                <td className="num mono">{l.econ.cac > 0 ? fmtZl(l.econ.cac) : '—'}</td>
                <td className="num mono">
                  {l.econ.mrr > 0 ? (
                    <span style={{ color: 'var(--acc)', fontWeight: 600 }}>{fmtZl(l.econ.mrr)}</span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="num cell-sub">{relDate(l.lastActivityAt)}</td>
              </tr>
            ))}

            {rows.length === 0 && preRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-cell">
                  Brak leadów{q ? ' dla tego wyszukiwania' : ' w tej aplikacji'} — dodaj pierwszego przyciskiem
                  „Nowy lead”.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      )}

      {adding ? <AddLeadModal onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

function AddLeadModal({ onClose }: { onClose: () => void }) {
  const { activeAppId, activeApp, openLead } = useShell();
  const queryClient = useQueryClient();
  const campaignsQuery = useQuery(orpc.campaigns.list.queryOptions());
  const campaigns = campaignsQuery.data ?? [];
  const plans = activeApp?.plans ?? [];

  const [f, setF] = useState({
    company: '',
    contact: '',
    phone: '',
    email: '',
    sourceId: '',
    planId: '',
  });

  // Default selects to the first option once data arrives.
  const sourceId = f.sourceId || campaigns[0]?.id || '';
  const planId = f.planId || plans[0]?.id || '';

  const createLead = useMutation(
    orpc.leads.create.mutationOptions({
      onSuccess: (lead) => {
        void queryClient.invalidateQueries({ queryKey: orpc.leads.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.dashboard.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.costs.key() });
        onClose();
        openLead(lead.id);
      },
    }),
  );

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const save = () => {
    if (!f.company.trim() || !activeAppId) return;
    createLead.mutate({
      appId: activeAppId,
      company: f.company.trim(),
      contact: f.contact.trim(),
      role: '',
      phone: f.phone.trim(),
      email: f.email.trim(),
      status: 'nowy',
      planId: planId || null,
      billing: 'monthly' as Billing,
      sourceId: sourceId || null,
    });
  };

  return (
    <Modal
      title="Nowy lead"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Anuluj
          </button>
          <button className="btn primary" onClick={save} disabled={!f.company.trim() || createLead.isPending}>
            Dodaj lead
          </button>
        </>
      }
    >
      <label className="fld">
        Firma
        <input className="input" value={f.company} onChange={set('company')} autoFocus placeholder="np. Ognik Serwis" />
      </label>
      <label className="fld">
        Osoba kontaktowa
        <input className="input" value={f.contact} onChange={set('contact')} />
      </label>
      <div className="fld-row">
        <label className="fld">
          Telefon
          <input className="input" value={f.phone} onChange={set('phone')} placeholder="+48 …" />
        </label>
        <label className="fld">
          E-mail
          <input className="input" value={f.email} onChange={set('email')} />
        </label>
      </div>
      <div className="fld-row">
        <label className="fld">
          Źródło
          <select className="input" value={sourceId} onChange={set('sourceId')}>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="fld">
          Plan (zainteresowanie)
          <select className="input" value={planId} onChange={set('planId')}>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {fmtZl(p.price)}/mc
              </option>
            ))}
          </select>
        </label>
      </div>
    </Modal>
  );
}
